// Shared "Now Playing Stage" hero used across media-heavy pages
// (Stations, Radio, future station-detail / track-detail pages).
//
// Behavior contract:
// - When `isActive` is true → render the full hero: cover artwork (or MP4 video
//   portaled in via `setVideoHost`), Web Audio visualizer, track meta, and
//   controls (Play/Pause/Stop/Skip/Thumbs).
// - When `isActive` is false → render the page-supplied `emptyState` slot
//   (e.g. Tune-In CTA on RadioPage, marketing intro on StationsPage).
// - Keep black/gold Yah-Wave styling. Pulse ring + scale animation on cover
//   while playing. Visualizer falls back to CSS EQ if Web Audio is blocked.
// - The video portal host MUST be set to null whenever this component is
//   inactive or unmounting, so the persistent player can re-host the video
//   elsewhere (mini-player) without DOM duplication.

import React, { useCallback, useEffect } from "react";
import { Play, Pause, SkipForward, Square, ThumbsUp, ThumbsDown } from "lucide-react";
import { coverUrl } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import Visualizer from "./Visualizer";
import WhyThisTrack from "./WhyThisTrack";
import UpNextPreview from "./UpNextPreview";

const DEFAULT_COVER = "https://images.unsplash.com/photo-1771747131849-41056d3d9be3?crop=entropy&cs=srgb&fm=jpg&w=800&q=85";

export default function NowPlayingStage({
  isActive,
  emptyState,
  testIdPrefix = "now-playing",
  onAirLabel,
  livePillLabels,
  extraInfoBelow,
}) {
  const {
    currentTrack, isPlaying, togglePlay, next, stopPlayback,
    stationName, reactions, thumbUp, thumbDown,
    isVideo, setVideoHost, audioRef, videoRef,
  } = usePlayer();

  // Always release the host on unmount (or when toggling to inactive).
  const videoHostRef = useCallback((el) => {
    setVideoHost(el || null);
  }, [setVideoHost]);
  useEffect(() => () => setVideoHost(null), [setVideoHost]);

  if (!isActive || !currentTrack) {
    return <>{emptyState}</>;
  }

  const myReaction = reactions[currentTrack.id] || null;
  const cover = coverUrl(currentTrack) || DEFAULT_COVER;
  const computedOnAir = onAirLabel || (stationName ? `On Air · ${stationName}` : "Now Playing");
  const playingLabel = livePillLabels?.playing || "LIVE";
  const pausedLabel = livePillLabels?.paused || "PAUSED";
  const activeMediaEl = isVideo ? videoRef?.current : audioRef?.current;

  return (
    <div
      data-testid={`${testIdPrefix}-active`}
      className="card-surface relative overflow-hidden bg-gradient-to-br from-[#1f1a14] via-[#0E1014] to-[#0a0a14] p-5 md:p-8"
    >
      {/* Background blurred cover for atmosphere */}
      <div
        className="absolute inset-0 opacity-40 pointer-events-none transition-opacity duration-700"
        style={{
          backgroundImage: `url(${cover})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(60px) saturate(1.4)",
        }}
        aria-hidden="true"
      />
      {/* On-Air status pill, top-right */}
      <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
        <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/60 backdrop-blur-sm border border-[#C5A059]/30 ${isPlaying ? "" : "opacity-60"}`}>
          <span className={`w-2 h-2 rounded-full bg-red-500 ${isPlaying ? "animate-pulse" : ""}`} />
          <span className="overline text-[10px] text-[#FDFBF7] tracking-widest">{isPlaying ? playingLabel : pausedLabel}</span>
        </div>
      </div>

      <div className="relative grid md:grid-cols-[380px_1fr] lg:grid-cols-[440px_1fr] gap-6 md:gap-8 items-center">
        <div className="mx-auto md:mx-0 w-full max-w-[420px] md:max-w-none">
          <div
            ref={videoHostRef}
            data-testid={`${testIdPrefix}-video-host`}
            className="relative aspect-square w-full rounded-2xl overflow-hidden border border-[#C5A059]/30 shadow-[0_0_60px_rgba(197,160,89,0.25)] bg-black"
          >
            {!isVideo && (
              <>
                <img
                  src={cover}
                  alt={`${currentTrack.title} cover`}
                  className={`absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ${isPlaying ? "scale-105" : "scale-100"}`}
                />
                {isPlaying && (
                  <div className="absolute inset-0 rounded-2xl ring-1 ring-[#C5A059]/40 pointer-events-none animate-[pulse_2.4s_ease-in-out_infinite]" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
              </>
            )}
          </div>
          <div className="mt-3 px-1">
            <Visualizer mediaEl={activeMediaEl} isPlaying={isPlaying} height={56} />
          </div>
        </div>

        <div className="flex flex-col justify-between min-w-0 text-center md:text-left">
          <div>
            <div className="flex items-center gap-2 mb-2 justify-center md:justify-start flex-wrap">
              <span className="overline text-[#C5A059] text-xs tracking-widest">{computedOnAir}</span>
              {currentTrack.media_type === "video" && (
                <span className="overline text-[10px] px-2 py-0.5 rounded-full border border-[#C5A059]/40 text-[#C5A059] bg-[#C5A059]/5">VIDEO</span>
              )}
              {currentTrack.mood && <span className="overline text-[10px] px-2 py-0.5 rounded-full border border-[#22262E] text-[#A1A4AB]">{currentTrack.mood}</span>}
              {currentTrack.energy && <span className="overline text-[10px] px-2 py-0.5 rounded-full border border-[#22262E] text-[#A1A4AB]">Energy {currentTrack.energy}/5</span>}
              {currentTrack.genre && <span className="overline text-[10px] px-2 py-0.5 rounded-full border border-[#22262E] text-[#A1A4AB]">{currentTrack.genre}</span>}
              <WhyThisTrack track={currentTrack} stationName={stationName} reactions={reactions} />
            </div>
            <h2
              data-testid={`${testIdPrefix}-title`}
              className="font-serif text-3xl md:text-5xl text-[#FDFBF7] tracking-tight leading-tight break-words"
            >
              {currentTrack.title}
            </h2>
            <div
              data-testid={`${testIdPrefix}-artist`}
              className="text-[#A1A4AB] mt-2 text-base md:text-lg"
            >
              {currentTrack.artist}
            </div>
            <UpNextPreview testIdPrefix={`${testIdPrefix}-up-next`} />
            {extraInfoBelow}
          </div>

          <div className="flex items-center gap-2 md:gap-3 mt-6 flex-wrap justify-center md:justify-start">
            <button
              onClick={togglePlay}
              data-testid={`${testIdPrefix}-play-toggle`}
              aria-label={isPlaying ? "Pause" : "Play"}
              className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#C5A059] text-[#060709] flex items-center justify-center hover:bg-[#DCC286] hover:scale-105 transition-all shadow-[0_0_30px_rgba(197,160,89,0.5)]"
            >
              {isPlaying ? <Pause className="w-6 h-6 md:w-7 md:h-7" strokeWidth={2} /> : <Play className="w-6 h-6 md:w-7 md:h-7 ml-0.5" strokeWidth={2} />}
            </button>
            <button
              onClick={stopPlayback}
              data-testid={`${testIdPrefix}-stop-btn`}
              title="Stop — fully halts playback and clears the station queue"
              aria-label="Stop"
              className="w-11 h-11 rounded-full border border-red-400/30 text-red-300 hover:bg-red-500/10 hover:border-red-400 transition-colors flex items-center justify-center"
            >
              <Square className="w-3.5 h-3.5" strokeWidth={2} fill="currentColor" />
            </button>
            <button
              onClick={next}
              data-testid={`${testIdPrefix}-skip-btn`}
              aria-label="Skip"
              title="Skip"
              className="w-11 h-11 rounded-full border border-[#22262E] text-[#A1A4AB] hover:text-[#FDFBF7] hover:border-[#C5A059]/40 transition-colors flex items-center justify-center"
            >
              <SkipForward className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <div className="w-px h-8 bg-[#22262E] mx-1" />
            <button
              onClick={thumbUp}
              data-testid={`${testIdPrefix}-thumb-up`}
              title="More like this"
              aria-label="Thumbs up"
              className={`w-11 h-11 rounded-full border transition-colors flex items-center justify-center ${myReaction === "like" ? "border-[#C5A059] bg-[#C5A059]/10 text-[#C5A059]" : "border-[#22262E] text-[#A1A4AB] hover:text-[#FDFBF7] hover:border-[#C5A059]/40"}`}
            >
              <ThumbsUp className="w-4 h-4" strokeWidth={1.5} fill={myReaction === "like" ? "#C5A059" : "none"} />
            </button>
            <button
              onClick={thumbDown}
              data-testid={`${testIdPrefix}-thumb-down`}
              title="Skip and play less of this"
              aria-label="Thumbs down"
              className={`w-11 h-11 rounded-full border transition-colors flex items-center justify-center ${myReaction === "dislike" ? "border-red-400 bg-red-500/10 text-red-300" : "border-[#22262E] text-[#A1A4AB] hover:text-red-300 hover:border-red-400/40"}`}
            >
              <ThumbsDown className="w-4 h-4" strokeWidth={1.5} fill={myReaction === "dislike" ? "rgb(252,165,165)" : "none"} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
